import { defineStore } from 'pinia';
import { computed, ref, type Ref } from 'vue';

export const ROLES = {
  ADMIN: 'Admin',
  USER: 'User'
} as const;

type UserRole = typeof ROLES[keyof typeof ROLES];
type PoloValue = string | string[] | Record<string, unknown> | null | undefined;

const normalizePolo = (polo: PoloValue): string[] => {
  if (Array.isArray(polo)) {
    return polo.flatMap(item => normalizePolo(item as PoloValue));
  }

  if (polo && typeof polo === 'object') {
    return Object.entries(polo).flatMap(([key, value]) => {
      if (typeof value === 'string' && value.toUpperCase() === 'POLO') {
        return [key];
      }

      return [key, ...normalizePolo(value as PoloValue)];
    });
  }

  if (typeof polo === 'string') {
    const trimmedPolo = polo.trim();

    if (trimmedPolo.startsWith('[') && trimmedPolo.endsWith(']')) {
      try {
        const parsedPolo = JSON.parse(trimmedPolo.replace(/'/g, '"'));

        if (Array.isArray(parsedPolo)) {
          return normalizePolo(parsedPolo);
        }
      } catch (error) {
        console.warn('Unable to parse polo array string:', error);
      }
    }

    return polo
      .split(',')
      .map(item => item.trim().replace(/^\[?['"]?/, '').replace(/['"]?\]?$/, ''))
      .filter(Boolean);
  }

  return [];
};

export const hasAdminPolo = (polo: PoloValue): boolean => {
  return normalizePolo(polo).some(item => item.toUpperCase().includes('ADMIN'));
};

export const hasSdPolo = (polo: PoloValue): boolean => {
  const normalizedPolo = normalizePolo(polo);
  return normalizedPolo.some(item => item.toUpperCase() === 'SD');
};

export const useRolesStore = defineStore('roles', () => {
  const polo: Ref<PoloValue> = ref(null);
  const userRole: Ref<UserRole | null> = ref(null);

  const setPolo = (rawPolo: PoloValue) => {
    polo.value = rawPolo;
    userRole.value = hasAdminPolo(rawPolo) ? ROLES.ADMIN : ROLES.USER;
  };

  const setUserRole = (role: UserRole) => {
    userRole.value = role;
  };

  const getPolo = computed(() => polo.value);
  const getUserRole = computed(() => userRole.value);
  const isAdmin = computed(() => userRole.value === ROLES.ADMIN);
  const isUser = computed(() => userRole.value === ROLES.USER);

  const hasNoPolo = computed(() => {
    if (!polo.value) {
      return true;
    }
    const normalized = normalizePolo(polo.value);
    return normalized.length === 0;
  });

  const hasSdPoloComputed = computed(() => {
    if (!polo.value) {
      return false;
    }
    return hasSdPolo(polo.value);
  });

  return {
    polo,
    userRole,
    getPolo,
    getUserRole,
    isAdmin,
    isUser,
    setPolo,
    setUserRole,
    hasNoPolo,
    hasSdPolo: hasSdPoloComputed
  };
});